import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import {
  displayToKg,
  getLocalePrefs,
  kgToDisplay,
  weightUnitLabel,
  type WeightUnit,
} from '@/lib/locale';
import { SlidersHorizontal, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

type WeightDialPickerProps = {
  open: boolean;
  onClose: () => void;
  /** Value and callbacks are in the *display* unit (kg or lb). */
  onConfirm: (value: number | null) => void;
  initial?: number | null;
  min?: number;
  max?: number;
  step?: number;
  title?: string;
  unitLabel?: string;
};

function clampAndSnap(raw: number, min: number, max: number, step: number, snap: boolean) {
  const clamped = Math.min(max, Math.max(min, raw));
  if (!snap) return clamped;
  return Math.round(clamped / step) * step;
}

function formatWeight(value: number, step: number) {
  const decimals = step < 1 ? 1 : 0;
  return value.toFixed(decimals);
}

export function WeightDialPicker({
  open,
  onClose,
  onConfirm,
  initial = null,
  min = 0,
  max = 1200,
  step = 0.5,
  title = 'Peso',
  unitLabel = 'kg',
}: WeightDialPickerProps) {
  const [value, setValue] = useState(() =>
    clampAndSnap(initial ?? 300, min, max, step, true),
  );
  const [manualText, setManualText] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{ lastX: number } | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const next = clampAndSnap(initial ?? 300, min, max, step, true);
    setValue(next);
    setManualText(formatWeight(next, step));
  }, [open, initial, min, max, step]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [open, onClose]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    if (cssWidth < 2 || cssHeight < 2) return;
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const arcCenter = { x: cssWidth / 2, y: cssHeight * 0.98 };
    const radius = cssWidth * 0.46;
    const startAngle = Math.PI + 0.35;
    const sweepAngle = Math.PI - 0.7;
    const visibleSpanKg = 50;
    const majorEvery = 10;

    ctx.beginPath();
    ctx.arc(arcCenter.x, arcCenter.y, radius, startAngle, startAngle + sweepAngle);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.stroke();

    const rangeStart = value - visibleSpanKg / 2;
    const rangeEnd = value + visibleSpanKg / 2;

    const drawTick = (angle: number, major: boolean, active: boolean) => {
      const outer = radius - 4;
      const inner = radius - (major ? 22 : 12);
      const p1 = {
        x: arcCenter.x + outer * Math.cos(angle),
        y: arcCenter.y + outer * Math.sin(angle),
      };
      const p2 = {
        x: arcCenter.x + inner * Math.cos(angle),
        y: arcCenter.y + inner * Math.sin(angle),
      };
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.strokeStyle = active ? '#52B788' : major ? 'rgba(61, 44, 36, 0.45)' : 'rgba(61, 44, 36, 0.22)';
      ctx.lineWidth = major ? 2.2 : 1.2;
      ctx.lineCap = 'round';
      ctx.stroke();
    };

    const drawLabel = (angle: number, label: string, active: boolean) => {
      const labelRadius = radius - 36;
      const pos = {
        x: arcCenter.x + labelRadius * Math.cos(angle),
        y: arcCenter.y + labelRadius * Math.sin(angle),
      };
      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.rotate(angle + Math.PI / 2);
      ctx.font = `${active ? 800 : 600} 13px ui-sans-serif, system-ui, sans-serif`;
      ctx.fillStyle = active ? '#1B4332' : 'rgba(61, 44, 36, 0.55)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, 0, 0);
      ctx.restore();
    };

    for (
      let kg = Math.floor(rangeStart / majorEvery) * majorEvery;
      kg <= rangeEnd + 0.01;
      kg += majorEvery
    ) {
      if (kg < min || kg > max) continue;
      const fraction = (kg - value) / (visibleSpanKg / 2);
      if (Math.abs(fraction) > 1.02) continue;
      const angle = startAngle + ((fraction + 1) / 2) * sweepAngle;
      const isCenter = Math.abs(kg - value) < majorEvery / 2;
      drawTick(angle, true, isCenter);
      drawLabel(angle, String(Math.round(kg)), isCenter);
    }

    for (let kg = Math.ceil(rangeStart); kg <= rangeEnd; kg += 2) {
      if (kg % majorEvery === 0) continue;
      if (kg < min || kg > max) continue;
      const fraction = (kg - value) / (visibleSpanKg / 2);
      if (Math.abs(fraction) > 1.02) continue;
      const angle = startAngle + ((fraction + 1) / 2) * sweepAngle;
      drawTick(angle, false, false);
    }

    const pointerAngle = startAngle + sweepAngle / 2;
    const pointerBase = {
      x: arcCenter.x + radius * Math.cos(pointerAngle),
      y: arcCenter.y + radius * Math.sin(pointerAngle),
    };
    const pointerTip = {
      x: arcCenter.x + (radius - 28) * Math.cos(pointerAngle),
      y: arcCenter.y + (radius - 28) * Math.sin(pointerAngle),
    };
    ctx.beginPath();
    ctx.moveTo(pointerTip.x, pointerTip.y);
    ctx.lineTo(pointerBase.x, pointerBase.y);
    ctx.strokeStyle = '#52B788';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.stroke();
  }, [value, min, max]);

  useEffect(() => {
    if (!open) return;
    draw();
  }, [open, draw]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, draw]);

  function applyDrag(deltaDx: number) {
    setValue((prev) => {
      const next = clampAndSnap(prev - deltaDx / 5, min, max, step, true);
      setManualText(formatWeight(next, step));
      return next;
    });
  }

  function commitTypedValue() {
    const parsed = Number(manualText.replace(',', '.'));
    if (Number.isNaN(parsed) || manualText.trim() === '') {
      setManualText(formatWeight(value, step));
      return;
    }
    const next = clampAndSnap(parsed, min, max, step, false);
    setValue(next);
    setManualText(formatWeight(next, step));
  }

  function resolveValue(): number | null {
    const parsed = Number(manualText.replace(',', '.'));
    if (!Number.isNaN(parsed) && manualText.trim() !== '') {
      return clampAndSnap(parsed, min, max, step, false);
    }
    return value;
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { lastX: e.clientX };
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dragRef.current) return;
    const delta = e.clientX - dragRef.current.lastX;
    dragRef.current.lastX = e.clientX;
    applyDrag(delta);
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-manejai-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal
        aria-labelledby={titleId}
        className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-elevated"
      >
        <div className="flex items-center justify-between border-b border-manejai-sand px-5 py-4">
          <h2 id={titleId} className="font-display text-lg font-semibold text-manejai-forest">
            {title}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Fechar">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-3 px-5 py-4">
          <p className="text-center text-sm text-manejai-bark/55">
            Arraste a escala ou toque no número para digitar
          </p>

          <div className="relative h-[220px] w-full overflow-hidden select-none">
            <div className="absolute left-1/2 top-0 z-10 flex -translate-x-1/2 flex-col items-center">
              <div className="flex h-14 min-w-[7.25rem] items-center justify-center rounded-[14px] border-2 border-manejai-sage bg-white shadow-[0_3px_8px_rgba(47,74,58,0.08)] focus-within:ring-2 focus-within:ring-manejai-sage/40">
                <input
                  id="weight-on-scale"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-label={`Peso em ${unitLabel}`}
                  value={manualText}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) => {
                    setManualText(e.target.value);
                    const parsed = Number(e.target.value.replace(',', '.'));
                    if (!Number.isNaN(parsed) && e.target.value.trim() !== '') {
                      setValue(clampAndSnap(parsed, min, max, step, false));
                    }
                  }}
                  onBlur={commitTypedValue}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  className="h-full w-[7.25rem] cursor-text bg-transparent px-2 text-center text-[28px] font-extrabold leading-none text-manejai-bark outline-none"
                />
              </div>
              <div
                className="pointer-events-none h-0 w-0 border-l-[9px] border-r-[9px] border-t-[10px] border-l-transparent border-r-transparent border-t-manejai-sage"
                aria-hidden
              />
            </div>
            <canvas
              ref={canvasRef}
              className="absolute inset-x-0 bottom-0 top-[68px] h-[calc(100%-68px)] w-full cursor-ew-resize touch-none"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-manejai-sand px-5 py-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" variant="ghost" onClick={() => onConfirm(null)}>
            Limpar
          </Button>
          <Button
            type="button"
            onClick={() => {
              const resolved = resolveValue();
              if (resolved == null) return;
              onConfirm(resolved);
            }}
          >
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
}

type WeightFieldProps = {
  label: string;
  /** Stored value always in kilograms (API). */
  value: string;
  onChange: (valueKg: string) => void;
  required?: boolean;
  title?: string;
  /** Max in kilograms. */
  maxKg?: number;
  step?: number;
  className?: string;
};

function formatDisplay(value: number, step: number) {
  const decimals = step < 1 ? 1 : 0;
  return value.toFixed(decimals);
}

export function WeightField({
  label,
  value,
  onChange,
  required,
  title,
  maxKg = 1200,
  step = 0.5,
  className,
}: WeightFieldProps) {
  const [open, setOpen] = useState(false);
  const inputId = label.toLowerCase().replace(/\s+/g, '-');
  const prefs = getLocalePrefs();
  const unit: WeightUnit = prefs.weightUnit;
  const unitLabel = weightUnitLabel(unit);

  const kgValue = useMemo(() => {
    if (value.trim() === '') return null;
    const parsed = Number(value.replace(',', '.'));
    return Number.isNaN(parsed) ? null : parsed;
  }, [value]);

  const displayValue =
    kgValue == null ? '' : formatDisplay(kgToDisplay(kgValue, unit), step);

  const [text, setText] = useState(displayValue);

  useEffect(() => {
    setText(displayValue);
  }, [displayValue]);

  const displayMax = Math.round(kgToDisplay(maxKg, unit));
  const displayInitial = kgValue == null ? null : kgToDisplay(kgValue, unit);

  function commitText(raw: string) {
    if (raw.trim() === '') {
      onChange('');
      setText('');
      return;
    }
    const parsed = Number(raw.replace(',', '.'));
    if (Number.isNaN(parsed)) {
      setText(displayValue);
      return;
    }
    const kg = displayToKg(parsed, unit);
    onChange(formatDisplay(kg, step));
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={inputId} className="block text-sm font-medium text-manejai-bark">
        {label}
        {required ? ' *' : ''}
      </label>
      <div className="relative">
        <input
          id={inputId}
          inputMode="decimal"
          autoComplete="off"
          value={text}
          placeholder={`Ex.: 387`}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => commitText(text)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          className={cn(
            'h-10 w-full rounded-xl border border-manejai-sand bg-white px-3 pr-16 text-sm',
            'placeholder:text-manejai-bark/40',
            'focus:outline-none focus:ring-2 focus:ring-manejai-sage/40 focus:border-manejai-sage',
          )}
        />
        <span className="pointer-events-none absolute inset-y-0 right-10 flex items-center text-xs text-manejai-bark/50">
          {unitLabel}
        </span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-manejai-moss hover:bg-manejai-cream hover:text-manejai-forest"
          aria-label="Abrir escala visual"
          title="Escala visual"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </button>
      </div>

      <WeightDialPicker
        open={open}
        onClose={() => setOpen(false)}
        initial={displayInitial}
        min={0}
        max={displayMax}
        step={step}
        unitLabel={unitLabel}
        title={title ?? `Peso (${unitLabel})`}
        onConfirm={(next) => {
          if (next == null) {
            onChange('');
          } else {
            const kg = displayToKg(next, unit);
            onChange(formatDisplay(kg, step));
          }
          setOpen(false);
        }}
      />
    </div>
  );
}
